/**
 * PaidAdvertisingPage.tsx
 * Purpose: Showcase paid advertising platforms we manage using provided brand logos.
 *
 * Notes:
 * - Circle badge size is kept constant while logos inside each badge are enlarged to be twice the previous size.
 * - Uses external image URLs supplied by the user, with lazy loading and proper alt attributes for accessibility.
 */

import React from 'react'
import { Link } from 'react-router'
import { Button } from '../../components/ui/button'
import { CheckCircle } from 'lucide-react'

/**
 * PaidAdvertisingPage
 * Main page component showcasing paid advertising platforms and services.
 *
 * @returns JSX.Element
 */
export default function PaidAdvertisingPage(): JSX.Element {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">Paid Advertising</h1>
              <p className="text-xl text-green-100 leading-relaxed mb-8">
                Maximize your ROI with data-driven paid advertising campaigns across Google Ads,
                Meta platforms, and other premium advertising networks.
              </p>
              <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
                <Link to="/contact">Launch Your Campaign</Link>
              </Button>
            </div>
            <div>
              <img
                src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/8bb0078e-4ed3-417b-8567-13982fe9b891.jpg"
                alt="Paid Advertising"
                className="w-full h-80 object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Platforms We Cover */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Advertising Platforms We Master</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert management across all major paid advertising platforms for maximum reach and efficiency
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Google Ads */}
            <div className="bg-orange-50 p-8 rounded-2xl text-center">
              <div className="bg-orange-100 w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                <img
                  src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/0bf0a7a6-6cd2-4e22-b94f-29c4d43c9217.png"
                  alt="Google Ads logo"
                  loading="lazy"
                  className="h-32 w-32 object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Google Ads</h3>
              <p className="text-gray-600">Search, Display, Shopping, and YouTube advertising campaigns</p>
            </div>

            {/* Meta Ads */}
            <div className="bg-indigo-50 p-8 rounded-2xl text-center">
              <div className="bg-indigo-100 w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                <img
                  src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/86e60936-de0d-475e-bd16-18c399d0e414.png"
                  alt="Meta Ads logo"
                  loading="lazy"
                  className="h-32 w-32 object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Meta Ads</h3>
              <p className="text-gray-600">Facebook and Instagram advertising with advanced targeting</p>
            </div>

            {/* LinkedIn Ads */}
            <div className="bg-purple-50 p-8 rounded-2xl text-center">
              <div className="bg-purple-100 w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                <img
                  src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/db389a6f-d427-430f-a960-490b6f0008e5.png"
                  alt="LinkedIn Ads logo"
                  loading="lazy"
                  className="h-32 w-32 object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">LinkedIn Ads</h3>
              <p className="text-gray-600">B2B targeted advertising for professional audiences</p>
            </div>

            {/* Snapchat Ads */}
            <div className="bg-red-50 p-8 rounded-2xl text-center">
              <div className="bg-red-100 w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                <img
                  src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/2d68dc54-b649-46ab-b60e-7f79bff3b936.jpg"
                  alt="Snapchat Ads logo"
                  loading="lazy"
                  className="h-32 w-32 object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Snapchat Ads</h3>
              <p className="text-gray-600">Short-form vertical video and interactive ad formats for younger audiences</p>
            </div>

            {/* X Ads */}
            <div className="bg-teal-50 p-8 rounded-2xl text-center">
              <div className="bg-teal-100 w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                <img
                  src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/32073891-5196-4023-8d71-708e3bb2a6f2.jpg"
                  alt="X Ads logo"
                  loading="lazy"
                  className="h-32 w-32 object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">X Ads</h3>
              <p className="text-gray-600">Real-time engagement and trend-based advertising</p>
            </div>

            {/* TikTok Ads */}
            <div className="bg-orange-50 p-8 rounded-2xl text-center">
              <div className="bg-orange-100 w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                <img
                  src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/69b65e4c-eefe-40fe-bb13-c3d931c49bdf.png"
                  alt="TikTok Ads logo"
                  loading="lazy"
                  className="h-32 w-32 object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">TikTok Ads</h3>
              <p className="text-gray-600">Creative video campaigns for younger demographics</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">What We Deliver</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Campaign Strategy & Setup</h3>
                    <p className="text-gray-600">Comprehensive campaign planning and account configuration</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Advanced Audience Targeting</h3>
                    <p className="text-gray-600">Precise targeting based on demographics, interests, and behaviors</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Ad Creative Development</h3>
                    <p className="text-gray-600">Compelling ad copy, visuals, and video content creation</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Continuous Optimization</h3>
                    <p className="text-gray-600">A/B testing, bid management, and performance optimization</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Detailed Reporting</h3>
                    <p className="text-gray-600">Regular performance reports with actionable insights</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/ffaafd26-dff3-4772-ba99-ddf40743e4ae.png"
                alt="Digital Advertising Analytics"
                className="w-full h-96 object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-900 to-emerald-800 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Ready to Scale Your Advertising?</h2>
          <p className="text-xl text-green-100 mb-8">
            Let our experts create and manage high-performing paid advertising campaigns for your business
          </p>
          <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600 text-lg px-8">
            <Link to="/contact">Get Started Today</Link>
          </Button>
        </div>
      </section>
    </div>
  )
}